import type { FileId } from '../../../shared/domain/files/types.no-deps'
import type { Upload, UploadGroupId } from './Upload'
import { concatOrSingleton } from '../../../shared/lib/functional/array.no-deps'
import { emptyDict } from '../../../shared/lib/functional/dictionary.no-deps'

type Callback<T> = (t: T) => void
type Callbacks<T> = Array<Callback<T>>

export class UploadSubscriptionManager {
    private subscribers = emptyDict<Callbacks<Upload>>()
    private groupSubscribers = emptyDict<Callbacks<Upload[]>>()
    private allSubscribers: Callbacks<Upload> = []

    public subscribe = (fileId: FileId, callback: Callback<Upload>) => {
        this.subscribers[fileId] = concatOrSingleton(this.subscribers[fileId], callback)
    }

    public unsubscribe = (fileId: FileId, callback: Callback<Upload>) => {
        unsubscribe(this.subscribers[fileId], callback)
    }

    public subscribeToGroup = (groupId: UploadGroupId, callback: Callback<Upload[]>) => {
        this.groupSubscribers[groupId] = concatOrSingleton(this.groupSubscribers[groupId], callback)
    }

    public unsubscribeToGroup = (groupsId: UploadGroupId, callback: Callback<Upload[]>) => {
        unsubscribe(this.groupSubscribers[groupsId], callback)
    }

    public subscribeToAll = (callback: Callback<Upload>) => {
        this.allSubscribers.push(callback)
    }

    public unsubscribeToAll = (callback: Callback<Upload>) => {
        unsubscribe(this.allSubscribers, callback)
    }

    public triggerSubscribers = (upload: Upload, groupUploads: Upload[]) => {
        const { fileId } = upload.fileUploadData
        const { groupId } = upload
        const subscribers = this.subscribers[fileId]
        if (subscribers && subscribers.length > 0) {
            subscribers.forEach((subscriber) => subscriber(upload))
        }

        if (groupId) {
            const groupSubscribers = this.groupSubscribers[groupId]

            if (groupSubscribers && groupSubscribers.length > 0) {
                groupSubscribers.forEach((subscriber) => subscriber(groupUploads))
            }
        }

        this.allSubscribers.forEach((subscriber) => subscriber(upload))
    }
}

function unsubscribe<T>(callbacks: Callbacks<T>, callback: Callback<T>) {
    const index = callbacks.indexOf(callback)
    if (index < 0) throw new Error('Subscriber does not exist')
    callbacks.splice(index, 1)
}
